import { useCallback, useEffect, useRef, useState } from "react";
import { discardPendingToken, getAccessToken, setPendingToken } from "../apiClient.js";
import {
  login,
  register,
  resendOtp,
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
  verifyOtp,
} from "../httpService.js";
import Button from "./button.jsx";
import Input from "./input.jsx";
import Modal from "./modal.jsx";

const MODES = {
  LOGIN: "login",
  REGISTER: "register",
  OTP_EMAIL_VERIFICATION: "otpEmailVerification",
  FORGOT_PASSWORD: "forgotPassword",
  OTP_FORGOT_PASSWORD: "otpForgotPassword",
};

/**
 * Shown on the login view when the unverified account's token ran out while it was on the code
 * step. The token lives 15 minutes and cannot be refreshed, so the code step has nothing left to
 * work with; logging in again re-sends the code and hands out a new token.
 */
const SESSION_EXPIRED_MESSAGE = "Din inloggning har gått ut. Logga in igen så skickar vi en ny kod.";

const isValidEmail = (value) => {
  // Basic email validation regex
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(value).toLowerCase());
};

const LoginOrRegisterModal = ({ onClose, onCompleted }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [mode, setMode] = useState(MODES.LOGIN);
  const [otpCode, setOtpCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [emailError, setEmailError] = useState("");

  // Whether the modal is still on screen. An answer that arrives after it was closed is ignored:
  // it must neither leave an unverified account's token behind nor log anybody in.
  const isActive = useRef(true);

  // Closing the modal in the middle of the OTP step must not leave the unverified account's
  // token behind. After a completed login this does nothing: that token belongs to the session.
  useEffect(() => {
    isActive.current = true;
    return () => {
      isActive.current = false;
      discardPendingToken();
    };
  }, []);

  /**
   * Move on to the code step with the token of an unverified account. That token is only good for
   * verifying the OTP code or asking for a new one.
   */
  const startEmailVerification = useCallback((pendingData) => {
    setPendingToken(pendingData);
    setErrorMessage("");
    setMode(MODES.OTP_EMAIL_VERIFICATION);
  }, []);

  /** The code step's token is gone or no longer accepted: send the user back to log in again. */
  const returnToLogin = useCallback(() => {
    discardPendingToken();
    setPassword("");
    setOtpCode("");
    setEmailError("");
    setErrorMessage(SESSION_EXPIRED_MESSAGE);
    setMode(MODES.LOGIN);
  }, []);

  const doLogin = useCallback(async () => {
    // Validate email before attempting login
    if (!isValidEmail(email)) {
      setEmailError("Ogiltig e-postadress");
      return;
    }
    try {
      const response = await login({
        email,
        password,
      });

      const data = await response.json();
      if (!isActive.current) return;

      if (data.verified) {
        onCompleted(data);
      } else {
        startEmailVerification(data);
      }
    } catch (_error) {
      setErrorMessage("Ett fel inträffade. Vänligen försök igen.");
    }
  }, [email, password, onCompleted, startEmailVerification]);

  const doRegister = useCallback(async () => {
    // Validate email before attempting register
    if (!isValidEmail(email)) {
      setEmailError("Ogiltig e-postadress");
      return;
    }
    try {
      const response = await register({
        email,
        password,
      });

      const data = await response.json();
      if (!isActive.current) return;

      startEmailVerification(data);
    } catch (_error) {
      setErrorMessage("Ett fel inträffade. Vänligen försök igen.");
    }
  }, [email, password, startEmailVerification]);

  const resendOtpCode = useCallback(async () => {
    if (getAccessToken() === null) {
      returnToLogin();
      return;
    }
    try {
      await resendOtp();
      if (!isActive.current) return;

      setErrorMessage("En ny kod har skickats till din e-postadress.");
    } catch (error) {
      if (!isActive.current) return;
      if (error?.status === 401) {
        returnToLogin();
      } else {
        setErrorMessage("Kunde inte skicka ny kod. Vänligen försök igen senare.");
      }
    }
  }, [returnToLogin]);

  const verifyOtpCode = useCallback(async () => {
    if (getAccessToken() === null) {
      returnToLogin();
      return;
    }
    try {
      const response = await verifyOtp(otpCode);
      const data = await response.json();
      if (!isActive.current) return;

      onCompleted(data);
    } catch (error) {
      if (!isActive.current) return;
      if (error?.status === 401) {
        returnToLogin();
      } else {
        setErrorMessage("Ett fel inträffade. Vänligen försök igen.");
      }
    }
  }, [otpCode, onCompleted, returnToLogin]);

  const verifyForgotPasswordOtpCode = useCallback(async () => {
    try {
      const response = await verifyForgotPasswordOtp(otpCode, email, newPassword);

      const data = await response.json();
      if (!isActive.current) return;

      if (data.verified) {
        onCompleted(data);
      } else {
        // Resetting the password does not verify the email address, so this account still has to
        // do that. The code it was sent at registration or login may be long gone (they last ten
        // minutes), so ask for a fresh one right away instead of leaving the user to find the link.
        startEmailVerification(data);
        await resendOtpCode();
      }
    } catch (_error) {
      setErrorMessage("Ett fel inträffade. Vänligen försök igen.");
    }
  }, [otpCode, onCompleted, email, newPassword, startEmailVerification, resendOtpCode]);

  const handleSendForgotPasswordOtp = useCallback(async () => {
    try {
      await sendForgotPasswordOtp(email);
      setMode(MODES.OTP_FORGOT_PASSWORD);
      setErrorMessage("");
    } catch (_error) {
      setErrorMessage("Ett fel inträffade. Vänligen försök igen.");
    }
  }, [email]);

  const renderLoginForm = () => (
    <div className="flex flex-col gap-4">
      {errorMessage && <div className="text-error text-sm">{errorMessage}</div>}
      <div>
        <Input
          type="email"
          id="email"
          label="Email"
          value={email}
          error={emailError}
          onChange={(event) => {
            const val = event.target.value;
            setEmail(val);
            // Clear error while typing; validate on blur instead
            setEmailError("");
          }}
          onBlur={(event) => {
            const val = event.target.value;
            if (!val) {
              setEmailError("");
            } else if (!isValidEmail(val)) {
              setEmailError("Ogiltig e-postadress");
            } else {
              setEmailError("");
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !emailError && isValidEmail(email)) {
              doLogin();
            }
          }}
        />
      </div>
      <div>
        <Input
          type="password"
          id="password"
          label="Lösenord"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !emailError && isValidEmail(email)) {
              doLogin();
            }
          }}
        />
      </div>
      <Button onClick={() => doLogin()} disabled={!email || !!emailError || !isValidEmail(email)}>
        Logga in
      </Button>
      <p className="text-sm">
        Har du inget konto?{" "}
        <button
          type="button"
          onClick={() => {
            setMode(MODES.REGISTER);
          }}
          className="text-primary underline"
        >
          Registrera dig
        </button>
      </p>
      <p className="text-sm">
        Glömt ditt lösenord?{" "}
        <button
          type="button"
          onClick={() => {
            setMode(MODES.FORGOT_PASSWORD);
            setErrorMessage("");
          }}
          className="text-primary underline"
        >
          Återställ här
        </button>
      </p>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="flex flex-col gap-4">
      {errorMessage && <div className="text-error text-sm">{errorMessage}</div>}
      <div>
        <Input
          type="email"
          id="email"
          label="Email"
          value={email}
          error={emailError}
          onChange={(event) => {
            const val = event.target.value;
            setEmail(val);
            // Clear error while typing; validate on blur instead
            setEmailError("");
          }}
          onBlur={(event) => {
            const val = event.target.value;
            if (!val) {
              setEmailError("");
            } else if (!isValidEmail(val)) {
              setEmailError("Ogiltig e-postadress");
            } else {
              setEmailError("");
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !emailError && isValidEmail(email)) {
              doRegister();
            }
          }}
        />
      </div>
      <div>
        <Input
          type="password"
          id="password"
          label="Lösenord"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !emailError && isValidEmail(email)) {
              doRegister();
            }
          }}
        />
      </div>
      <Button
        onClick={() => doRegister()}
        disabled={!email || !!emailError || !isValidEmail(email)}
      >
        Registrera
      </Button>
      <p className="text-sm">
        Har du redan ett konto?{" "}
        <button
          type="button"
          onClick={() => {
            setMode(MODES.LOGIN);
          }}
          className="text-primary underline"
        >
          Logga in
        </button>
      </p>
    </div>
  );

  const renderOtpEmailVerification = () => (
    <div className="flex flex-col gap-4">
      {errorMessage && <div className="text-error text-sm">{errorMessage}</div>}
      <p>Ange den 6-siffriga koden som skickats till din e-postadress.</p>
      <div>
        <Input
          type="text"
          id="otpCode"
          label="Verifieringskod"
          value={otpCode}
          onChange={(event) => setOtpCode(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              verifyOtpCode();
            }
          }}
        />
      </div>
      <Button onClick={() => verifyOtpCode()}>Verifiera</Button>
      <p className="text-sm">
        Har du inte fått koden?{" "}
        <button
          type="button"
          onClick={async () => {
            await resendOtpCode();
          }}
          className="text-primary underline"
        >
          Skicka koden igen
        </button>
      </p>
    </div>
  );

  const renderOtpForgotPasswordVerification = () => (
    <div className="flex flex-col gap-4">
      {errorMessage && <div className="text-error text-sm">{errorMessage}</div>}
      <p>Ange den 6-siffriga koden som skickats till din e-postadress.</p>
      <div className="flex flex-col gap-4">
        <div>
          <Input
            type="text"
            id="otpCode"
            label="Verifieringskod"
            value={otpCode}
            onChange={(event) => setOtpCode(event.target.value)}
          />
        </div>
        <div>
          <Input
            type="password"
            id="password"
            label="Nytt Lösenord"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </div>
      </div>
      <Button onClick={() => verifyForgotPasswordOtpCode()}>Verifiera</Button>
      <p className="text-sm">
        Har du inte fått koden?{" "}
        <button
          type="button"
          onClick={async () => {
            await sendForgotPasswordOtp(email);
            setErrorMessage("En ny kod har skickats till din e-postadress.");
          }}
          className="text-primary underline"
        >
          Skicka koden igen
        </button>
      </p>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <div className="flex flex-col gap-4">
      {errorMessage && <div className="text-error text-sm">{errorMessage}</div>}
      <p>Ange din e-postadress för att återställa ditt lösenord.</p>
      <div>
        <Input
          type="text"
          id="email"
          label="Email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSendForgotPasswordOtp();
            }
          }}
        />
      </div>
      <Button onClick={handleSendForgotPasswordOtp}>Skicka verifieringskod</Button>
      <p className="text-sm">
        Kommit på lösenordet?{" "}
        <button
          type="button"
          onClick={() => {
            setMode(MODES.LOGIN);
            setErrorMessage("");
          }}
          className="text-primary underline"
        >
          Logga in
        </button>
      </p>
    </div>
  );

  const renderContent = () => {
    switch (mode) {
      case MODES.LOGIN:
        return renderLoginForm();
      case MODES.REGISTER:
        return renderRegisterForm();
      case MODES.OTP_EMAIL_VERIFICATION:
        return renderOtpEmailVerification();
      case MODES.OTP_FORGOT_PASSWORD:
        return renderOtpForgotPasswordVerification();
      case MODES.FORGOT_PASSWORD:
        return renderForgotPasswordForm();
      default:
        return null;
    }
  };

  const getHeader = () => {
    switch (mode) {
      case MODES.LOGIN:
        return "Logga in";
      case MODES.REGISTER:
        return "Registrera dig";
      case MODES.OTP_EMAIL_VERIFICATION:
      case MODES.OTP_FORGOT_PASSWORD:
        return "Verifiera din e-postadress";
      case MODES.FORGOT_PASSWORD:
        return "Glömt Lösenord";
      default:
        return "";
    }
  };

  return (
    <Modal header={getHeader()} showCloseButton={false} onClose={onClose}>
      {renderContent()}
    </Modal>
  );
};

export default LoginOrRegisterModal;
